import { IStringDictionary, StringDictionary } from "../../core/StringDictionary";
import { Currency } from "../Currency";
import { Cart } from "./Cart";
import { ItemGroupCodes, ItemMedium, ItemNumber, ItemVendorCodes } from "./ItemNumber";
import { Price } from "./Price";
import { DiscountType } from "./PriceDiscount";
import { ProductPropertyKeys } from "./ProductProperty";

export class CartItem implements ICartItem {
    constructor(cart: Cart, item: ICartItem = null) {
        this._cart = cart;
        this.sequence = item?.sequence;
        this.itemId = item?.itemId;
        if (item?.productNumber)
            this.productNumber = item.productNumber instanceof ItemNumber ? item.productNumber : new ItemNumber(item.productNumber);
        this.unitCode = item?.unitCode;
        this.description = item?.description;
        this.quantity = item?.quantity ?? 1;
        this.manualDiscountAmount = item?.manualDiscountAmount;
        this.taxAmount = item?.taxAmount;
        if (item?.properties)
            this.properties = item.properties instanceof StringDictionary ? item.properties : new StringDictionary(item.properties);

        this.manualEdit = item?.manualEdit ?? false;
    }

    _cart: Cart;

    sequence: number;
    itemId: string;
    productNumber: ItemNumber;
    unitCode: string;
    description: string;
    quantity: number;
    manualDiscountAmount: number;
    taxAmount: number;
    properties: StringDictionary;

    manualEdit: boolean;

    toJSON(): object {
        return {
            //_cart Don't included cart, that would likely cause infinite loops
            sequence: this.sequence,
            itemId: this.itemId,
            productNumber: this.productNumber.toString(), //This is an object with it's own properties, but we just want the string representation
            description: this.description,
            unitCode: this.unitCode,
            quantity: this.quantity != 1 ? this.quantity : undefined,
            manualDiscountAmount: this.manualDiscountAmount,
            taxAmount: this.taxAmount,
            properties: this.properties,
            unitPrice: this.unitPrice,
            fulfillmentProvider: this.fulfillmentProvider,
            volumeDiscountAmount: this.volumeDiscountAmount,

            manualEdit: this.manualEdit
        }
    }

    getlocalizedTitle(culture: string): string {
        return this.pricing?.product?.getLocalizedTitle(culture);
    }

    //Begin Calculated Properties
    private _volumeDiscountAmount: number;
    get volumeDiscountAmount(): number {
        if (this.pricing == null)
            return this._volumeDiscountAmount;
        const level = this.pricing.discount?.findLevel(this.quantity, false);
        if (level != null)
            return (level.amountOrPercent *
                (this.quantity - (level.compound ? level.minQuantity : 0)) *
                Currency.round((this.pricing.discount.discountType === DiscountType.Percent ? this.unitPrice : 1)), this.pricing.currencyCode, this.pricing.roundingPolicy);
        return 0;
    }
    set volumeDiscountAmount(value: number) {
        this._volumeDiscountAmount = value;
    }

    protected _unitPrice: number;
    get unitPrice(): number {
        return this.pricing?.unitPrice ?? this._unitPrice;
    }
    set unitPrice(value: number) {
        this._unitPrice = value;
    }

    private _fulfillmentProvider: string;
    get fulfillmentProvider(): string {
        return this.pricing?.product?.fulfillmentProvider ?? this._fulfillmentProvider;
    }
    set fulfillmentProvider(value: string) {
        this._fulfillmentProvider = value;
    }

    private _pricing: Price;
    get pricing() {
        return this._pricing ?? (this._pricing = this._cart?.pricing?.priceList?.findPrice(this.productNumber, this.unitCode));
    }

    get title(): string {
        return this.pricing?.product?.title;
    }

    get thumbnailUrl(): string {
        return this.pricing?.product?.thumbnailUrl;
    }

    get informationUrl(): string {
        return this.pricing?.product?.informationUrl;
    }

    get unitType(): string {
        return this.pricing?.product?.unitType;
    }

    get currencyCode(): string {
        return this.pricing?.currencyCode ?? this._cart?.currencyCode;
    }

    get baseAmount(): number {
        return this.unitPrice * this.quantity;
    }

    get discountAmount(): number {
        return this.volumeDiscountAmount + (this.manualDiscountAmount ?? 0);
    }

    get discountedBaseAmount(): number {
        return this.baseAmount - this.discountAmount;
    }

    get unitDiscountAmount(): number {
        return this.quantity > 0 ? this.discountAmount / this.quantity : 0;
    }

    get extendedAmount(): number {
        return this.discountedBaseAmount + this.taxAmount;
    }

    get parentItem(): CartItem {
        if (this.isGroupLicenseItem)
            return null;
        let groupItem: CartItem = null;
        for (let i = 0; i < this._cart.items.length; i++) {
            let item = this._cart.items[i];
            if (item.isGroupLicenseItem)
                groupItem = item;
            else if (this === item && this.unitPrice === 0 && (this.productNumber).medium === ItemMedium.Online)
                return groupItem;
        }
        return null;
    }

    get isGroupLicenseItem(): boolean {
        return this.productNumber.vendorCode === ItemVendorCodes.TestOut &&
            this.productNumber.groupCode === ItemGroupCodes.GroupLicense;
    }

    get isGroupLicenseBasedItem(): boolean {
        return (this.productNumber.vendorCode === ItemVendorCodes.TestOut &&
            this.productNumber.groupCode === ItemGroupCodes.GroupLicense) ||
                (this.unitPrice === 0 && this.productNumber.medium === ItemMedium.Online &&
                this._cart.hasGroupLicenseBasedItems);
    }

    get isIndividualLicenseBasedItem(): boolean {
        return this.productNumber.medium === ItemMedium.Online && !this.isGroupLicenseBasedItem;
    }

    get hasUserProperty(): boolean {
        return this.properties.containsKey(ProductPropertyKeys.User);
    }

    get userProperty(): string {
        return this.properties[ProductPropertyKeys.User];
    }

    get optionProperties(): StringDictionary {
        const result = new StringDictionary();
        this.properties.forEach(prop => {
            if (prop.Key != ProductPropertyKeys.User)
                result.set(prop.Key, prop.Value);
        });
        return result;
    }
    //End Calculated Properties
}

export interface ICartItem {
    sequence: number;
    itemId: string;
    productNumber: ItemNumber | string;
    unitCode: string;
    description: string;
    quantity: number;
    manualDiscountAmount: number;
    taxAmount: number;
    properties: IStringDictionary;
    unitPrice: number;
    volumeDiscountAmount: number;
    fulfillmentProvider: string;

    manualEdit: boolean;
}